import { CardBgColor, CardHighlightColor, CardTextColor, generatedColors } from '../../constants';
import { Theme } from '../theme/theme.service';

export interface ChartPalette {
  cardBg: { [key: string]: string };
  cardHighlight: { [key: string]: string };
  cardText: { [key: string]: string };
  generated: Set<string>;
}

export interface ChartChrome {
  foreColor: string;
  gridBorderColor: string;
  tooltipTheme: 'light' | 'dark';
  /**
   * Gap drawn between donut slices. It has to match the surface behind the
   * chart, not be a colour of its own — ApexCharts defaults it to #fff, which
   * reads as a white outline on a dark card.
   */
  sliceStrokeColor: string;
  /**
   * Threshold lines drawn across a chart (the QPS Plan marker). Stronger than
   * foreColor: this one carries meaning, it is not chrome.
   */
  annotationLineColor: string;
}

// Dark counterparts, same hue, raised until they read on #1b1b1b.
// #4d75ff is the existing --sh-accent-text value from html.dark in _tokens.scss.
const ACCENT = '#4d75ff';
const TEAL = '#35c2cb';
const PURPLE = '#c264cb';
const CYAN = '#3ae0d6';
const SLATE = '#8fa3c7';
const HIGHLIGHT = '#b47dff';

const darkCardBg: { [key: string]: string } = {
  demand_bid_qps: TEAL,
  supply_bid_qps: ACCENT,
  incoming_requests: ACCENT,
  valid_qps: TEAL,
  outgoing_requests: PURPLE,
  today: ACCENT,
  yesterday: CYAN,
  two_days_ago: SLATE,
  ssp_requests: ACCENT,
  requests: ACCENT,
  responses: ACCENT,
  timeout_rate: ACCENT,
  total_bid_rate: ACCENT,
  bid_rate: ACCENT,
  fill_rate: ACCENT,
  imps: ACCENT,
  profit: ACCENT,
  render_rate: ACCENT,
  ssp_ecpm: ACCENT,
  dsp_ecpm: ACCENT,
  ssp_rcpm: ACCENT,
  dsp_rcpm: ACCENT,
  ssp_win_rate: ACCENT,
  dsp_win_rate: ACCENT,
  ssp_wins: ACCENT,
  wins: ACCENT,
};

const darkCardHighlight: { [key: string]: string } = Object.fromEntries(
  Object.keys(CardHighlightColor).map((key) => [key, HIGHLIGHT]),
);

// Text drawn on the fills above, not on the page background, so #fff carries
// over. two_days_ago is the exception: #D3D3D3 on the lightened #8fa3c7 is
// 1.7:1, while #16213a is 6.3:1.
const darkCardText: { [key: string]: string } = {
  ...CardTextColor,
  two_days_ago: '#16213a',
};

// Same order as the light set — performance/sources index it positionally.
// 'rgb(255, 167, 79)' appears twice for the same reason the light set repeats
// 'rgb(255, 128, 0)': the Set collapses both to one entry, keeping the sizes equal.
const darkGenerated = new Set([
  'rgb(56, 205, 229)',
  'rgb(167, 106, 255)',
  'rgb(97, 122, 255)',
  'rgb(224, 108, 108)',
  'rgb(92, 199, 102)',
  'rgb(122, 141, 240)',
  'rgb(255, 167, 79)',
  'rgb(202, 116, 202)',
  'rgb(77, 190, 190)',
  'rgb(163, 163, 255)',
  'rgb(198, 198, 88)',
  'rgb(255, 167, 79)',
  'rgb(96, 170, 170)',
  'rgb(170, 170, 80)',
  'rgb(178, 96, 178)',
]);

export const CHART_PALETTE: Record<Theme, ChartPalette> = {
  // Light values are imported, never retyped, so this file cannot move the light theme.
  light: {
    cardBg: CardBgColor,
    cardHighlight: CardHighlightColor,
    cardText: CardTextColor,
    generated: generatedColors,
  },
  dark: {
    cardBg: darkCardBg,
    cardHighlight: darkCardHighlight,
    cardText: darkCardText,
    generated: darkGenerated,
  },
};

// The light column reproduces the ApexCharts defaults in effect today: no
// dashboard service sets foreColor, grid.borderColor or tooltip.theme, so
// writing them explicitly leaves light rendering unchanged.
export const CHART_CHROME: Record<Theme, ChartChrome> = {
  light: {
    foreColor: '#373d3f',
    gridBorderColor: '#e0e0e0',
    tooltipTheme: 'light',
    sliceStrokeColor: '#fff',
    annotationLineColor: '#000',
  },
  dark: {
    foreColor: 'rgba(255, 255, 255, 0.65)',
    gridBorderColor: '#303030',
    tooltipTheme: 'dark',
    // --sh-bg-surface in html.dark: the chart containers are transparent, so
    // this is the card showing through.
    sliceStrokeColor: '#1b1b1b',
    // --sh-text in html.dark.
    annotationLineColor: 'rgba(255, 255, 255, 0.85)',
  },
};
